import { names } from "../../interfaces/names";
import { Button } from "../UI/Button/Button";
import useInput from "../../hooks/use-input";

import classes from "./EmployeeForm.module.scss";
import { IStaff } from "../../interfaces/staff";

interface IForm {
  initValues: IStaff;
  Card?: boolean;
  disabled?: boolean;
  maxDate: string;
  minDate: string;
  cancelButton: () => void;
  submitButton: (obj: IStaff) => void;
  editButton?: () => void;
  removeButton?: () => void;
  className?: string;
}

const isEmpty = (value) =>
  value.trim() && value.length >= 2 && value.length < 12;

const EmployeeForm = (props: IForm) => {
  const {
    value: nameValue,
    blurHandler: nameBlurHandler,
    changeHandler: nameChangeHandler,
    isValid: nameIsValid,
    reset: nameReset,
    inputClasses: nameInputClasses,
  } = useInput(isEmpty, classes, props.initValues.name);

  const {
    value: surnameValue,
    blurHandler: surnameBlurHandler,
    changeHandler: surnameChangeHandler,
    isValid: surnameIsValid,
    reset: surnameReset,
    inputClasses: surnameInputClasses,
  } = useInput(isEmpty, classes, props.initValues.surname);

  const {
    value: middleNameValue,
    blurHandler: middleNameBlurHandler,
    changeHandler: middleNameChangeHandler,
    isValid: middleNameIsValid,
    reset: middleNameReset,
    inputClasses: middleNameInputClasses,
  } = useInput(isEmpty, classes, props.initValues.middleName);

  const {
    value: departmentValue,
    blurHandler: departmentBlurHandler,
    changeHandler: departmentChangeHandler,
    isValid: departmentIsValid,
    reset: departmentReset,
    inputClasses: departmentInputClasses,
  } = useInput(isEmpty, classes, props.initValues.department);

  const {
    value: genderValue,
    blurHandler: genderBlurHandler,
    changeHandler: genderChangeHandler,
    isValid: genderIsValid,
    reset: genderReset,
    inputClasses: genderInputClasses,
  } = useInput(isEmpty, classes, props.initValues.gender);

  const {
    value: posValue,
    blurHandler: posBlurHandler,
    changeHandler: posChangeHandler,
    isValid: posIsValid,
    reset: posReset,
    inputClasses: posInputClasses,
  } = useInput(isEmpty, classes, props.initValues.pos);

  const {
    value: dateValue,
    blurHandler: dateBlurHandler,
    changeHandler: dateChangeHandler,
    isValid: dateIsValid,
    reset: dateReset,
    inputClasses: dateInputClasses,
  } = useInput(isEmpty, classes, props.initValues.date, false);

  const formIsValid =
    nameIsValid &&
    surnameIsValid &&
    middleNameIsValid &&
    departmentIsValid &&
    genderIsValid &&
    posIsValid &&
    dateIsValid;

  const initValue = () => {
    nameReset();
    surnameReset();
    middleNameReset();
    departmentReset();
    genderReset();
    posReset();
    dateReset();
  };

  const cancelButtonHandler = () => {
    props.cancelButton();
    initValue();
  };

  const editButtonHandler = () => {
    if (props.editButton) {
      props.editButton();
    }
  };

  const removeButtonHandler = () => {
    if (props.removeButton) {
      props.removeButton();
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();

    if (!formIsValid) {
      return;
    }

    props.submitButton({
      name: nameValue,
      surname: surnameValue,
      middleName: middleNameValue,
      department: departmentValue,
      gender: genderValue,
      pos: posValue,
      date: dateValue,
    });

    initValue();
  };

  return (
    <form
      className={`${classes.EmployeeForm} ${props.disabled && "view"} ${
        props.className
      }`}
      onSubmit={submitHandler}
    >
      <div
        className={`${classes.EmployeeForm__controls} ${props.Card && "Card"}`}
      >
        <div className={nameInputClasses}>
          <label>
            <strong>Имя:</strong>
          </label>
          <input
            disabled={props.disabled}
            onChange={nameChangeHandler}
            value={nameValue}
            onBlur={nameBlurHandler}
            type="text"
          ></input>
        </div>
        <div className={surnameInputClasses}>
          <label>
            <strong>Фамилия:</strong>
          </label>
          <input
            disabled={props.disabled}
            onChange={surnameChangeHandler}
            onBlur={surnameBlurHandler}
            value={surnameValue}
            type="text"
          ></input>
        </div>
        <div className={middleNameInputClasses}>
          <label>
            <strong>Отчество:</strong>
          </label>
          <input
            disabled={props.disabled}
            onChange={middleNameChangeHandler}
            value={middleNameValue}
            onBlur={middleNameBlurHandler}
            type="text"
          ></input>
        </div>
        <div className={departmentInputClasses}>
          <label>
            <strong>Отдел:</strong>
          </label>
          <select
            disabled={props.disabled}
            onChange={departmentChangeHandler}
            onBlur={departmentBlurHandler}
            value={departmentValue}
          >
            <option value="" disabled hidden>
              Please Choose...
            </option>
            <option>{names.IT}</option>
            <option>{names.Бухгалтерия}</option>
            <option>{names.Маркетинг}</option>
          </select>
        </div>
        <div className={genderInputClasses}>
          <label>
            <strong>Пол:</strong>
          </label>
          <div className={classes.EmployeeForm__radios}>
            <span>
              <input
                disabled={props.disabled}
                id={names.Мужчина}
                checked={genderValue === names.Мужчина}
                name="gender"
                type="radio"
                onBlur={genderBlurHandler}
                value={names.Мужчина}
                onChange={genderChangeHandler}
              />
              <label htmlFor={names.Мужчина}>{names.Мужчина}</label>
            </span>
            <span>
              <input
                disabled={props.disabled}
                id={names.Женщина}
                name="gender"
                type="radio"
                value={names.Женщина}
                onBlur={genderBlurHandler}
                onChange={genderChangeHandler}
                checked={genderValue === names.Женщина}
              />
              <label htmlFor={names.Женщина}>{names.Женщина}</label>
            </span>
          </div>
        </div>
        <div className={posInputClasses}>
          <label>
            <strong>Должность:</strong>
          </label>
          <input
            disabled={props.disabled}
            onChange={posChangeHandler}
            onBlur={posBlurHandler}
            value={posValue}
            type="text"
          ></input>
        </div>
        <div className={dateInputClasses}>
          <label>
            <strong>Birthday:</strong>
          </label>
          <input
            disabled={props.disabled}
            onChange={dateChangeHandler}
            onBlur={dateBlurHandler}
            value={dateValue}
            type="date"
            max={props.maxDate}
            min={props.minDate}
          ></input>
        </div>
      </div>
      {props.disabled ? (
        <div className={classes.EmployeeForm__buttons}>
          <Button onClick={editButtonHandler} type="button">
            Edit
          </Button>
          <Button onClick={removeButtonHandler} type="button">
            Remove
          </Button>
        </div>
      ) : (
        <div className={classes.EmployeeForm__buttons}>
          <Button onClick={cancelButtonHandler} type="button">
            Cancel
          </Button>
          <Button type="submit" disabled={!formIsValid}>
            Accept
          </Button>
        </div>
      )}
    </form>
  );
};

export default EmployeeForm;
